import React, { useEffect } from 'react';
import Spacings from '~/modules/ui-kit/spacings';
import { TablePagination } from '@mui/material';

export interface PaginationControlsWithQueryProps {
  controlsClassName?: string;
  initialPage?: number;
  perPage?: number;
  totalCount: number;
  isLoading?: boolean;
  onChangePage: (page: number) => void;
  onChangeRowsPerPage?: (perPage: number) => void;
}

export const PaginationControlsWithQuery: React.FC<
  PaginationControlsWithQueryProps
> = ({
  controlsClassName,
  initialPage = 0,
  perPage = 20,
  totalCount = 0,
  isLoading = false,
  onChangePage,
  onChangeRowsPerPage,
}) => {
  // Controlled component: `initialPage` is the source of truth for the current
  // page. Every page change is reported via `onChangePage`, and consumers feed
  // it back through `initialPage`.

  // Reset page to 0 if current page exceeds max page when totalCount changes
  useEffect(() => {
    const maxPage = Math.ceil(totalCount / perPage) - 1;
    if (initialPage > maxPage && maxPage >= 0) {
      onChangePage(0);
    }
  }, [totalCount, perPage, initialPage, onChangePage]);

  const handlePageChange = (_event: unknown, newPage: number) => {
    onChangePage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newPerPage = parseInt(event.target.value, 10);
    if (onChangeRowsPerPage) {
      onChangeRowsPerPage(newPerPage);
    }
    onChangePage(0);
  };

  return (
    <Spacings.Inset scale="s" className={controlsClassName}>
      {!isLoading ? (
        <TablePagination
          component="div"
          count={totalCount}
          page={initialPage}
          onPageChange={handlePageChange}
          rowsPerPage={perPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[20, 50, 100]}
        />
      ) : null}
    </Spacings.Inset>
  );
};

export default PaginationControlsWithQuery;
